/**
 * Routage applicatif.
 *
 * L'écran de connexion est le seul rendu hors `AppShell`. En mode bypass le serveur
 * répond « authentifié » d'emblée, donc l'écran de connexion n'apparaît jamais en
 * développement — c'est exactement l'effet attendu.
 */

import { Navigate, Route, Routes, useMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSession } from './contexts/SessionContext.jsx';
import { AppShell } from './components/AppShell.jsx';
import { ActivationPage } from './pages/ActivationPage.jsx';
import { LoginPage } from './pages/LoginPage.jsx';
import { ProjectsPage } from './pages/ProjectsPage.jsx';
import { ProjectPage } from './pages/ProjectPage.jsx';
import { OverviewPage } from './pages/OverviewPage.jsx';
import { TeamsPage } from './pages/TeamsPage.jsx';
import { TeamDetailPage } from './pages/TeamDetailPage.jsx';
import { AccountPage } from './pages/AccountPage.jsx';
import { OrganizationPage } from './pages/OrganizationPage.jsx';
import { CreateOrganizationPage } from './pages/CreateOrganizationPage.jsx';
import { SharedProjectPage } from './pages/SharedProjectPage.jsx';
import { AcceptInvitationPage } from './pages/AcceptInvitationPage.jsx';
import { AideMcpPage } from './pages/AideMcpPage.jsx';
import { ConfidentialitePage } from './pages/ConfidentialitePage.jsx';
import { EYEBROW } from './ui/theme.js';

function Centered({ title, children }) {
  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <div className="relative border-2 border-line bg-paper p-6 text-center">
        <span className="ticks" aria-hidden="true" />
        <p className={EYEBROW}>{title}</p>
        <p className="mt-2 text-sm text-ink-soft">{children}</p>
      </div>
    </div>
  );
}

export function App() {
  const { t } = useTranslation();
  const { status } = useSession();
  // Pages publiques statiques : aucune donnée de compte, donc rendues AVANT l'attente de la
  // session — un visiteur (ou le relecteur d'un annuaire) ne doit jamais voir « chargement »
  // ni « serveur injoignable » à la place de la documentation. Les deux hooks sont appelés
  // SANS court-circuit : un `||` sauterait le second selon l'URL (règle des hooks).
  const matchAide = useMatch('/aide-mcp');
  const matchConfidentialite = useMatch('/confidentialite');
  const pagePubliqueStatique = matchAide !== null || matchConfidentialite !== null;

  if (pagePubliqueStatique) {
    return (
      <Routes>
        <Route path="/aide-mcp" element={<AideMcpPage />} />
        <Route path="/confidentialite" element={<ConfidentialitePage />} />
      </Routes>
    );
  }

  if (status === 'loading') {
    return <Centered title="exiPlan">{t('session.loading')}</Centered>;
  }
  if (status === 'offline') {
    return (
      <Centered title={t('session.offlineTitle')}>
        {t('session.offlineBodyPrefix')}
        <code className="font-mono">npm run dev</code>
        {t('session.offlineBodySuffix')}
      </Centered>
    );
  }

  // Compte sans rôle exiplan.utilisateur (§ 3.5.3 de la spec CIAM) : rendue AVANT tout ce qui
  // suit, jamais dans `AppShell` — aucune navigation applicative ne doit être possible tant que
  // le rôle n'est pas là. Seule `/partage/:token` reste accessible : elle ne porte jamais de
  // jeton (D5) et ne dépend donc pas de ce statut. `/invitation/:token`, elle, EXIGE le rôle
  // (l'acceptation est un geste authentifié) : elle affiche `ActivationPage` comme le reste,
  // et redevient joignable normalement une fois le rôle acquis — après « Réessayer », `status`
  // passe à `authenticated` et la branche ci-dessous reprend la main sur cette même URL
  // (revue qualité du lot 2, point majeur n°4).
  if (status === 'activating' || status === 'suspended') {
    return (
      <Routes>
        <Route path="/partage/:token" element={<SharedProjectPage />} />
        <Route path="*" element={<ActivationPage />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Vue publique par lien de partage : accessible sans session. */}
      <Route path="/partage/:token" element={<SharedProjectPage />} />
      {/* Aperçu d'une invitation, PUIS acceptation : l'invité la consulte avant même d'être
          connecté — même logique que `/partage/:token`, hors du garde d'authentification. */}
      <Route path="/invitation/:token" element={<AcceptInvitationPage />} />
      {/* `/confidentialite` (MCP-U4) et `/aide-mcp` (URL du Claude Directory) sont publiques :
          rendues plus haut, avant l'attente de session, pour tous les statuts. */}

      {status === 'anonymous' ? (
        <>
          <Route path="/connexion" element={<LoginPage />} />
          <Route path="*" element={<Navigate to="/connexion" replace />} />
        </>
      ) : (
        <>
          <Route path="/" element={<Navigate to="/projets" replace />} />
          <Route path="/connexion" element={<Navigate to="/projets" replace />} />
          <Route
            path="/projets"
            element={
              <AppShell>
                <ProjectsPage />
              </AppShell>
            }
          />
          <Route
            path="/projets/:projectId/*"
            element={
              <AppShell>
                <ProjectPage />
              </AppShell>
            }
          />
          <Route
            path="/vue-ensemble"
            element={
              <AppShell>
                <OverviewPage />
              </AppShell>
            }
          />
          <Route
            path="/equipes"
            element={
              <AppShell>
                <TeamsPage />
              </AppShell>
            }
          />
          <Route
            path="/equipes/:teamId"
            element={
              <AppShell>
                <TeamDetailPage />
              </AppShell>
            }
          />
          <Route
            path="/compte"
            element={
              <AppShell>
                <AccountPage />
              </AppShell>
            }
          />
          {/* Anciennes routes : le Profil et les Préférences sont désormais deux onglets d'une
              même destination « Compte » (§ audit exiUI 2026-09-27). */}
          <Route path="/profil" element={<Navigate to="/compte?onglet=profil" replace />} />
          <Route
            path="/preferences"
            element={<Navigate to="/compte?onglet=preferences" replace />}
          />
          <Route
            path="/organisation/nouvelle"
            element={
              <AppShell>
                <CreateOrganizationPage />
              </AppShell>
            }
          />
          <Route
            path="/organisation/:organizationId"
            element={
              <AppShell>
                <OrganizationPage />
              </AppShell>
            }
          />
          <Route path="*" element={<Navigate to="/projets" replace />} />
        </>
      )}
    </Routes>
  );
}
